import { useEffect, useRef } from 'react';

const stats = [
  { value: '108,000+', label: 'Audio sessions in dataset', icon: 'ri-database-2-line' },
  { value: '16kHz', label: 'Low recording settings', icon: 'ri-record-circle-line' },
  { value: '0.902', label: 'Average AUC score', icon: 'ri-line-chart-line' },
  { value: '81.4%', label: 'Sensitivity', icon: 'ri-eye-line' },
  { value: '86.6%', label: 'Specificity', icon: 'ri-shield-check-line' },
  { value: '6', label: 'Mental health hospitals', icon: 'ri-hospital-line' },
];

export default function PlatformSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="platform"
      className="relative w-full bg-background-100 py-20 md:py-32 px-6 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <div className="reveal opacity-0 mb-4">
            <span className="font-body text-xs font-semibold tracking-widest text-primary-500 uppercase">
              Platform
            </span>
          </div>
          <h2 className="reveal opacity-0 font-display text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-50 leading-tight mb-4">
            World&apos;s First Regulatory Approved SaMD
          </h2>
          <p className="reveal opacity-0 font-body text-base text-foreground-400 max-w-3xl mx-auto">
            We have the world&apos;s first regulatory approved SaMD for depression risk assessment via voice, and we are developing new applications for other chronic diseases using our state-of-the-art voice AI technologies.
          </p>
        </div>

        {/* Stats grid */}
        <div className="reveal opacity-0 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-16">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-500/30 transition-all duration-500 ease-silk text-center group"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-500/10 mb-3">
                <i className={`${stat.icon} text-primary-500 text-lg`} />
              </div>
              <div className="font-display text-2xl md:text-3xl font-medium text-foreground-50 mb-1">
                {stat.value}
              </div>
              <div className="font-body text-xs text-foreground-600">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Platform features */}
        <div className="reveal opacity-0 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-500/10 mb-4">
              <i className="ri-smartphone-line text-accent-500 text-xl" />
            </div>
            <h3 className="font-body text-lg font-semibold text-foreground-50 mb-3">
              Optimized for Mobile First
            </h3>
            <p className="font-body text-sm text-foreground-400 leading-relaxed mb-4">
              AI model and related technology was carefully designed to optimize for mobile first environments. All voice data were collected from patient&apos;s own mobile devices. The model only requires low recording settings (16KHz, 16-bit, mono audio), supported by most smartphones, smart wearables, and smart home devices.
            </p>
            <div className="flex items-center gap-2 text-xs font-body text-foreground-500">
              <i className="ri-check-line text-accent-500" />
              <span>16-bit, mono audio support</span>
            </div>
            <div className="flex items-center gap-2 text-xs font-body text-foreground-500 mt-1">
              <i className="ri-check-line text-accent-500" />
              <span>Works on smartphones, wearables, smart speakers</span>
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-500/10 mb-4">
              <i className="ri-user-voice-line text-accent-500 text-xl" />
            </div>
            <h3 className="font-body text-lg font-semibold text-foreground-50 mb-3">
              Voice Dataset from Real Patients
            </h3>
            <p className="font-body text-sm text-foreground-400 leading-relaxed mb-4">
              Voice data were collected from real patients diagnosed by psychiatrists following DSM-5 standards from six different mental health instritutes. Oizys dataset now contains more than 108,000 audio sessions.
            </p>
            <div className="flex items-center gap-2 text-xs font-body text-foreground-500">
              <i className="ri-check-line text-accent-500" />
              <span>DSM-5 diagnostic standards</span>
            </div>
            <div className="flex items-center gap-2 text-xs font-body text-foreground-500 mt-1">
              <i className="ri-check-line text-accent-500" />
              <span>In partnership with globally renowned medical institutes</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}